import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
}

interface EmptyStateProps {
  icon: LucideIcon;
  /** Why it's empty, in a few words ("Ningún prospecto coincide"). */
  title: string;
  description?: React.ReactNode;
  /** The recovery step: clear filters, create the first record… */
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** Less vertical padding, for table cells and side panels. */
  compact?: boolean;
  className?: string;
}

/**
 * List / search zero state. Every empty list should say why it's empty and
 * offer the next step, never just "No se encontraron resultados".
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  compact = false,
  className,
}) => {
  const { theme } = useTheme();
  const isLight = theme !== 'dark';

  return (
    <div className={clsx('flex flex-col items-center justify-center text-center px-4', compact ? 'py-10' : 'py-16', className)}>
      <div className={clsx(
        'w-12 h-12 rounded-xl flex items-center justify-center mb-4',
        isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-slate-500'
      )}>
        <Icon size={24} />
      </div>
      <h3 className={clsx('text-base font-bold mb-1', isLight ? 'text-slate-900' : 'text-white')}>{title}</h3>
      {description && (
        <p className={clsx('text-sm max-w-md', isLight ? 'text-slate-500' : 'text-slate-400')}>{description}</p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
            >
              {primaryAction.icon && <primaryAction.icon size={16} />}
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors',
                isLight
                  ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
                  : 'border-white/10 text-slate-200 hover:bg-white/5'
              )}
            >
              {secondaryAction.icon && <secondaryAction.icon size={16} />}
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
